import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import CloudOutlinedIcon from '@mui/icons-material/CloudOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DnsOutlinedIcon from '@mui/icons-material/DnsOutlined';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import {
  assignMemberToOrg,
  createAdminOrg,
  createOrgConnection,
  deleteOrgConnection,
  inviteOrgMember,
  listAdminMembers,
  listAdminOrgs,
  listOrgConnections,
  listOrgMembers,
  setOrgMemberGrants,
  setOrgMemberRole,
  setOrgMemberStatus,
  updateOrgConnection,
  type OrgMember,
  type OrgSummary,
} from '@services/api/r2ApiClient';
import { ConnectionDialog } from '@components/ConnectionDialog';
import { useOrg } from '@hooks/auth/useOrg';
import { getMemberStorageAccessSummary } from './orgManagementAccess';
import type { AdminMember, ConnectionGrant, SavedConnection, SavedConnectionProtocol } from '@/types/connection.types';

const STATUS_COLORS: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
  approved: 'success',
  pending: 'warning',
  blocked: 'error',
};

const PROTOCOL_LABEL: Record<SavedConnectionProtocol, string> = {
  r2: 'Cloudflare R2',
  s3: 'AWS S3',
  'dreamhost-object': 'DreamObjects',
  minio: 'MinIO',
  sftp: 'SFTP',
};

function ProtocolIcon({ protocol, fontSize = 'small' }: { protocol: SavedConnectionProtocol; fontSize?: 'small' | 'medium' }) {
  if (protocol === 'sftp') return <DnsOutlinedIcon fontSize={fontSize} />;
  if (protocol === 'minio') return <FolderOpenOutlinedIcon fontSize={fontSize} />;
  return <CloudOutlinedIcon fontSize={fontSize} />;
}

function orgInitials(name: string): string {
  const trimmed = String(name || '').trim();
  if (!trimmed) return '?';
  const parts = trimmed.split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() || '').join('') || trimmed[0]!.toUpperCase();
}

function buildFullAccessGrants(connections: SavedConnection[]): ConnectionGrant[] {
  return connections.map((connection) => ({
    connectionId: connection.id,
    allowedPrefixes: [''],
    permissions: ['read', 'write', 'delete'],
  }));
}

function buildReadOnlyGrants(connections: SavedConnection[]): ConnectionGrant[] {
  return connections.map((connection) => ({
    connectionId: connection.id,
    allowedPrefixes: [''],
    permissions: ['read'],
  }));
}

export function OrgManagement() {
  const theme = useTheme();
  const { org: activeOrg } = useOrg();
  const isPlatformOwner = activeOrg?.platformRole === 'platform_owner' || !!activeOrg?.isAdmin;
  const [orgs, setOrgs] = useState<OrgSummary[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<OrgSummary | null>(null);
  const [orgMembers, setOrgMembers] = useState<OrgMember[]>([]);
  const [allMembers, setAllMembers] = useState<AdminMember[]>([]);
  const [orgConnections, setOrgConnections] = useState<SavedConnection[]>([]);
  const [selectedMemberEmail, setSelectedMemberEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [connectionDialogOpen, setConnectionDialogOpen] = useState(false);
  const [editingConnection, setEditingConnection] = useState<SavedConnection | null>(null);

  const memberCountByOrg = useMemo(() => {
    const map = new Map<string, number>();
    for (const member of allMembers) {
      if (!member.orgId) continue;
      map.set(member.orgId, (map.get(member.orgId) || 0) + 1);
    }
    return map;
  }, [allMembers]);

  const detailBackends = useMemo(() => {
    const set = new Set<string>();
    for (const c of orgConnections) set.add(PROTOCOL_LABEL[c.protocol] || c.protocol.toUpperCase());
    return [...set];
  }, [orgConnections]);

  const refreshAdminData = useCallback(async () => {
    try {
      const [nextOrgs, nextMembers] = await Promise.all([
        listAdminOrgs(),
        listAdminMembers(),
      ]);
      setOrgs(nextOrgs);
      setAllMembers(nextMembers);
      setSelectedOrg((current) => {
        if (current) return nextOrgs.find((org) => org.id === current.id) || nextOrgs[0] || null;
        return nextOrgs[0] || null;
      });
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    }
  }, []);

  const loadOrgDetail = useCallback(async (org: OrgSummary) => {
    try {
      const [members, connections] = await Promise.all([
        listOrgMembers(org.id),
        listOrgConnections(org.id),
      ]);
      setOrgMembers(members);
      setOrgConnections(connections);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    }
  }, []);

  useEffect(() => {
    void refreshAdminData();
  }, [refreshAdminData]);

  useEffect(() => {
    if (!selectedOrg) {
      setOrgMembers([]);
      setOrgConnections([]);
      return;
    }
    void loadOrgDetail(selectedOrg);
  }, [loadOrgDetail, selectedOrg]);

  useEffect(() => {
    setSelectedMemberEmail('');
  }, [selectedOrg?.id]);

  const handleCreateOrg = useCallback(async () => {
    if (!newOrgName.trim()) return;
    try {
      const org = await createAdminOrg(newOrgName.trim());
      setNewOrgName('');
      setCreating(false);
      setSelectedOrg(org);
      await refreshAdminData();
    } catch (err) {
      setError((err as Error).message);
    }
  }, [newOrgName, refreshAdminData]);

  const handleAssignMember = useCallback(async () => {
    if (!selectedOrg || !selectedMemberEmail) return;
    try {
      if (isPlatformOwner) {
        await assignMemberToOrg(selectedMemberEmail, selectedOrg.id);
      } else {
        await inviteOrgMember(selectedOrg.id, selectedMemberEmail);
      }
      setSelectedMemberEmail('');
      await refreshAdminData();
      await loadOrgDetail(selectedOrg);
    } catch (err) {
      setError((err as Error).message);
    }
  }, [isPlatformOwner, loadOrgDetail, refreshAdminData, selectedMemberEmail, selectedOrg]);

  const handleStatusToggle = useCallback(async (member: OrgMember) => {
    if (!selectedOrg) return;
    const newStatus = member.status === 'approved' ? 'pending' : 'approved';
    try {
      await setOrgMemberStatus(selectedOrg.id, member.email, newStatus);
      await refreshAdminData();
      await loadOrgDetail(selectedOrg);
    } catch (err) {
      setError((err as Error).message);
    }
  }, [loadOrgDetail, refreshAdminData, selectedOrg]);

  const handleSaveConnection = useCallback(async (connection: SavedConnection) => {
    if (!selectedOrg) return;
    setError(null);
    if (editingConnection) {
      await updateOrgConnection(selectedOrg.id, editingConnection.id, connection);
    } else {
      await createOrgConnection(selectedOrg.id, connection);
    }
    setConnectionDialogOpen(false);
    setEditingConnection(null);
    await loadOrgDetail(selectedOrg);
  }, [editingConnection, loadOrgDetail, selectedOrg]);

  const handleDeleteConnection = useCallback(async (connection: SavedConnection) => {
    if (!selectedOrg) return;
    try {
      await deleteOrgConnection(selectedOrg.id, connection.id);
      await loadOrgDetail(selectedOrg);
    } catch (err) {
      setError((err as Error).message);
    }
  }, [loadOrgDetail, selectedOrg]);

  const handleGrantFullAccess = useCallback(async (member: OrgMember) => {
    if (!selectedOrg) return;
    try {
      await setOrgMemberGrants(selectedOrg.id, member.email, buildFullAccessGrants(orgConnections));
      await refreshAdminData();
      await loadOrgDetail(selectedOrg);
    } catch (err) {
      setError((err as Error).message);
    }
  }, [loadOrgDetail, orgConnections, refreshAdminData, selectedOrg]);

  const handleSetStorageAccess = useCallback(async (member: OrgMember, level: 'read' | 'upload') => {
    if (!selectedOrg || orgConnections.length === 0) return;
    const grants = level === 'upload'
      ? buildFullAccessGrants(orgConnections)
      : buildReadOnlyGrants(orgConnections);
    try {
      await setOrgMemberGrants(selectedOrg.id, member.email, grants);
      await refreshAdminData();
      await loadOrgDetail(selectedOrg);
    } catch (err) {
      setError((err as Error).message);
    }
  }, [loadOrgDetail, orgConnections, refreshAdminData, selectedOrg]);

  const handleSetRole = useCallback(async (member: OrgMember, role: 'member' | 'admin') => {
    if (!selectedOrg) return;
    try {
      await setOrgMemberRole(selectedOrg.id, member.email, role);
      await refreshAdminData();
      await loadOrgDetail(selectedOrg);
    } catch (err) {
      setError((err as Error).message);
    }
  }, [loadOrgDetail, refreshAdminData, selectedOrg]);

  return (
    <Stack spacing={2}>
      <ConnectionDialog
        open={connectionDialogOpen}
        initialConnection={editingConnection}
        onClose={() => { setConnectionDialogOpen(false); setEditingConnection(null); }}
        onSave={handleSaveConnection}
      />

      {error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}

      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography sx={{ fontWeight: 700 }}>Organizations</Typography>
        <Stack direction="row" spacing={1}>
          <Button size="small" startIcon={<RefreshRoundedIcon />} onClick={() => { void refreshAdminData(); }}>
            Refresh
          </Button>
          {isPlatformOwner && (
            <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)}>
              Create Org
            </Button>
          )}
        </Stack>
      </Stack>

      {isPlatformOwner && creating && (
        <Card className="glass-card" sx={{ borderRadius: 2 }}>
          <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
            <Stack spacing={2}>
              <Typography sx={{ fontWeight: 600 }}>New Organization</Typography>
              <TextField
                size="small"
                label="Name"
                value={newOrgName}
                onChange={(event) => setNewOrgName(event.target.value)}
              />
              <Stack direction="row" spacing={1}>
                <Button size="small" variant="contained" onClick={() => { void handleCreateOrg(); }}>
                  Create
                </Button>
                <Button size="small" onClick={() => { setCreating(false); setNewOrgName(''); }}>
                  Cancel
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      )}

      {orgs.length === 0 && !creating ? (
        <Card className="glass-card" sx={{ borderRadius: 2 }}>
          <CardContent sx={{ p: { xs: 4, md: 6 }, '&:last-child': { pb: { xs: 4, md: 6 } }, textAlign: 'center' }}>
            <Stack spacing={2} alignItems="center">
              <ApartmentOutlinedIcon sx={{ fontSize: 56, color: 'text.secondary', opacity: 0.7 }} />
              <Stack spacing={0.5} alignItems="center">
                <Typography sx={{ fontWeight: 700, fontSize: 18 }}>No organizations yet</Typography>
                <Typography sx={{ color: 'text.secondary', maxWidth: 380 }}>
                  Organizations group storage connections and the people allowed to use them. Create one to begin assigning storage.
                </Typography>
              </Stack>
              {isPlatformOwner && (
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)}>
                  Create your first organization
                </Button>
              )}
            </Stack>
          </CardContent>
        </Card>
      ) : (
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '300px 1fr' }, gap: 2 }}>
        <Stack spacing={1}>
          {orgs.map((org) => {
              const isSelected = selectedOrg?.id === org.id;
              const count = memberCountByOrg.get(org.id) || 0;
              return (
                <Card
                  key={org.id}
                  className="glass-card"
                  sx={{
                    borderRadius: 2,
                    cursor: 'pointer',
                    transition: 'background-color 160ms ease, border-color 160ms ease',
                    border: isSelected
                      ? `1px solid ${alpha(theme.palette.primary.main, 0.55)}`
                      : '1px solid transparent',
                    backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.08) : undefined,
                    boxShadow: isSelected ? `inset 3px 0 0 ${theme.palette.primary.main}` : undefined,
                  }}
                  onClick={() => setSelectedOrg(org)}
                >
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar
                        variant="rounded"
                        sx={{
                          width: 36,
                          height: 36,
                          fontSize: 13,
                          fontWeight: 700,
                          bgcolor: alpha(theme.palette.primary.main, isSelected ? 0.22 : 0.12),
                          color: theme.palette.primary.main,
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                        }}
                      >
                        {orgInitials(org.name)}
                      </Avatar>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontWeight: 700 }} noWrap>{org.name}</Typography>
                        <Typography
                          sx={{ fontSize: 11, color: 'text.secondary', fontFamily: 'JetBrains Mono, ui-monospace, monospace' }}
                          noWrap
                        >
                          {org.id}
                        </Typography>
                      </Box>
                      <Tooltip title={`${count} member${count === 1 ? '' : 's'}`}>
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'text.secondary' }}>
                          <PeopleAltOutlinedIcon sx={{ fontSize: 14 }} />
                          <Typography
                            sx={{ fontSize: 12, fontVariantNumeric: 'tabular-nums', minWidth: 14, textAlign: 'right' }}
                          >
                            {count}
                          </Typography>
                        </Stack>
                      </Tooltip>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
        </Stack>

        {selectedOrg ? (
          <Stack spacing={2}>
            <Card className="glass-card" sx={{ borderRadius: 2 }}>
              <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar
                    variant="rounded"
                    sx={{
                      width: 48,
                      height: 48,
                      fontSize: 18,
                      fontWeight: 700,
                      bgcolor: alpha(theme.palette.primary.main, 0.18),
                      color: theme.palette.primary.main,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                    }}
                  >
                    {orgInitials(selectedOrg.name)}
                  </Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="h6" noWrap sx={{ lineHeight: 1.2 }}>{selectedOrg.name}</Typography>
                    <Typography
                      sx={{ fontSize: 12, color: 'text.secondary', fontFamily: 'JetBrains Mono, ui-monospace, monospace' }}
                      noWrap
                    >
                      {selectedOrg.id}
                    </Typography>
                  </Box>
                </Stack>
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  flexWrap="wrap"
                  useFlexGap
                  sx={{
                    mt: 2,
                    pt: 2,
                    borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
                  }}
                >
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: 'text.secondary' }}>
                    <PeopleAltOutlinedIcon sx={{ fontSize: 16 }} />
                    <Typography sx={{ fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>
                      <Box component="span" sx={{ color: 'text.primary', fontWeight: 700, mr: 0.5 }}>
                        {orgMembers.length}
                      </Box>
                      member{orgMembers.length === 1 ? '' : 's'}
                    </Typography>
                  </Stack>
                  <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: alpha(theme.palette.primary.main, 0.3) }} />
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: 'text.secondary' }}>
                    <StorageOutlinedIcon sx={{ fontSize: 16 }} />
                    <Typography sx={{ fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>
                      <Box component="span" sx={{ color: 'text.primary', fontWeight: 700, mr: 0.5 }}>
                        {orgConnections.length}
                      </Box>
                      connection{orgConnections.length === 1 ? '' : 's'}
                    </Typography>
                  </Stack>
                  {detailBackends.length > 0 && (
                    <>
                      <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: alpha(theme.palette.primary.main, 0.3) }} />
                      <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                        {detailBackends.join(' · ')}
                      </Typography>
                    </>
                  )}
                </Stack>
              </CardContent>
            </Card>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }}>
              <TextField
                size="small"
                type="email"
                label="Member email"
                value={selectedMemberEmail}
                onChange={(event) => setSelectedMemberEmail(event.target.value)}
                sx={{ minWidth: 280, flex: 1 }}
              />
              <Button
                size="small"
                variant="contained"
                disabled={!selectedMemberEmail.trim()}
                onClick={() => { void handleAssignMember(); }}
              >
                Invite to Org
              </Button>
            </Stack>

            <Divider />

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography sx={{ fontWeight: 600, fontSize: 14, color: 'text.secondary' }}>
                Storage ({orgConnections.length})
              </Typography>
              {isPlatformOwner && (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => { setError(null); setEditingConnection(null); setConnectionDialogOpen(true); }}
                >
                  Add Storage
                </Button>
              )}
            </Stack>

            {orgConnections.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 2,
                  borderRadius: 2,
                  border: `1px dashed ${alpha(theme.palette.primary.main, 0.18)}`,
                  color: 'text.secondary',
                }}
              >
                <StorageOutlinedIcon sx={{ fontSize: 22, opacity: 0.7 }} />
                <Typography sx={{ fontSize: 13 }}>
                  No storage connections yet. Add one so members can transfer files.
                </Typography>
              </Box>
            ) : (
              <Stack spacing={1}>
                {orgConnections.map((connection) => {
                  const bucket = connection.bucket || connection.prefix || '';
                  const protocolLabel = PROTOCOL_LABEL[connection.protocol] || connection.protocol.toUpperCase();
                  return (
                    <Card key={connection.id} className="glass-card" sx={{ borderRadius: 2 }}>
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                            <Box
                              sx={{
                                width: 36,
                                height: 36,
                                display: 'grid',
                                placeItems: 'center',
                                borderRadius: 1.5,
                                color: theme.palette.primary.main,
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                bgcolor: alpha(theme.palette.primary.main, 0.06),
                                flexShrink: 0,
                              }}
                            >
                              <ProtocolIcon protocol={connection.protocol} />
                            </Box>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Typography sx={{ fontWeight: 700 }} noWrap>{connection.nickname}</Typography>
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label={protocolLabel}
                                  sx={{
                                    fontSize: 10,
                                    height: 20,
                                    borderColor: alpha(theme.palette.primary.main, 0.4),
                                    color: theme.palette.primary.main,
                                  }}
                                />
                              </Stack>
                              <Typography
                                sx={{
                                  fontSize: 12,
                                  color: 'text.secondary',
                                  fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                                }}
                                noWrap
                              >
                                {bucket ? `${bucket} · ` : ''}{connection.endpoint || '—'}
                              </Typography>
                            </Box>
                          </Stack>
                          {isPlatformOwner && (
                            <Stack direction="row" spacing={0.5}>
                              <Tooltip title="Edit connection">
                                <IconButton size="small" onClick={() => { setError(null); setEditingConnection(connection); setConnectionDialogOpen(true); }}>
                                  <DriveFileRenameOutlineIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Remove from org">
                                <IconButton size="small" color="error" onClick={() => { void handleDeleteConnection(connection); }}>
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          )}
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            )}

            <Divider />

            <Typography sx={{ fontWeight: 600, fontSize: 14, color: 'text.secondary' }}>
              Members ({orgMembers.length})
            </Typography>
            {orgMembers.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 2,
                  borderRadius: 2,
                  border: `1px dashed ${alpha(theme.palette.primary.main, 0.18)}`,
                  color: 'text.secondary',
                }}
              >
                <PeopleAltOutlinedIcon sx={{ fontSize: 22, opacity: 0.7 }} />
                <Typography sx={{ fontSize: 13 }}>
                  No members yet. Assign one above so they can use this org's storage.
                </Typography>
              </Box>
            ) : (
              <Stack spacing={1}>
                {orgMembers.map((member) => {
                  const statusColor = STATUS_COLORS[member.status] || 'default';
                  const initial = (member.name || member.email || '?').trim().charAt(0).toUpperCase() || '?';
                  const storageAccess = getMemberStorageAccessSummary(member, orgConnections);
                  const isApproved = member.status === 'approved';
                  const storageInteractive = orgConnections.length > 0 && isApproved;
                  const nextStorageLevel: 'read' | 'upload' = storageAccess.hasUploadAccess ? 'read' : 'upload';
                  const storedRole = (member.role || 'member').toLowerCase();
                  const currentRole = storedRole === 'owner' ? 'admin' : storedRole;
                  const roleInteractive = true;
                  const nextRole: 'member' | 'admin' = currentRole === 'admin' ? 'member' : 'admin';
                  return (
                    <Card key={member.email} className="glass-card" sx={{ borderRadius: 2 }}>
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                            <Avatar
                              src={member.picture || undefined}
                              alt={member.name || member.email}
                              sx={{
                                width: 36,
                                height: 36,
                                fontSize: 14,
                                fontWeight: 700,
                                bgcolor: alpha(theme.palette.primary.main, 0.14),
                                color: theme.palette.primary.main,
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
                              }}
                            >
                              {initial}
                            </Avatar>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography sx={{ fontWeight: 700 }} noWrap>{member.name || member.email}</Typography>
                              <Typography sx={{ fontSize: 12, color: 'text.secondary' }} noWrap>{member.email}</Typography>
                            </Box>
                          </Stack>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" justifyContent="flex-end" useFlexGap>
                            {orgConnections.length > 0 && storageAccess.chip && (
                              <Tooltip
                                title={storageInteractive
                                  ? `Click to change to ${nextStorageLevel === 'upload' ? 'Can upload' : 'Can read'}`
                                  : storageAccess.chip.tooltip}
                              >
                                <Chip
                                  size="small"
                                  label={storageAccess.chip.label}
                                  color={storageAccess.chip.color}
                                  variant={storageAccess.chip.variant}
                                  onClick={storageInteractive
                                    ? () => { void handleSetStorageAccess(member, nextStorageLevel); }
                                    : undefined}
                                  sx={storageInteractive ? { cursor: 'pointer' } : undefined}
                                />
                              </Tooltip>
                            )}
                            {orgConnections.length > 0 && !storageAccess.chip && storageAccess.actionLabel && (
                              <Button size="small" variant="outlined" onClick={() => { void handleGrantFullAccess(member); }}>
                                {storageAccess.actionLabel}
                              </Button>
                            )}
                            <Tooltip
                              title={roleInteractive
                                ? `Click to change role to ${nextRole}`
                                : 'Owner role cannot be changed here'}
                            >
                              <Chip
                                size="small"
                                label={currentRole}
                                variant="outlined"
                                onClick={roleInteractive
                                  ? () => { void handleSetRole(member, nextRole); }
                                  : undefined}
                                sx={{ textTransform: 'capitalize', cursor: roleInteractive ? 'pointer' : 'default' }}
                              />
                            </Tooltip>
                            <Tooltip title={`Click to mark as ${member.status === 'approved' ? 'pending' : 'approved'}`}>
                              <Chip
                                size="small"
                                label={member.status}
                                color={statusColor}
                                variant={member.status === 'approved' ? 'filled' : 'outlined'}
                                onClick={() => { void handleStatusToggle(member); }}
                                sx={{ textTransform: 'capitalize', cursor: 'pointer' }}
                              />
                            </Tooltip>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            )}
          </Stack>
        ) : (
          <Card className="glass-card" sx={{ borderRadius: 2 }}>
            <CardContent sx={{ p: 4, '&:last-child': { pb: 4 }, textAlign: 'center' }}>
              <Stack spacing={1.5} alignItems="center">
                <ApartmentOutlinedIcon sx={{ fontSize: 40, color: 'text.secondary', opacity: 0.6 }} />
                <Typography sx={{ color: 'text.secondary' }}>
                  Select an organization on the left to view members and storage.
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        )}
      </Box>
      )}
    </Stack>
  );
}
